import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import SidebarGroup from './SidebarGroup';

// El componente no mide el viewport a propósito: `menuOpen` significa
// "el usuario abrió el menú", no "estoy en mobile". En escritorio el CSS
// ignora este estado y el sidebar queda fijo (ver layouts/_sidebar.scss).
const Sidebar = ({ groupInfo, icons, currentUser = {}, logout = '' }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState('');

  const drawerRef = useRef(null);
  const burgerRef = useRef(null);
  const touchStartX = useRef(null);

  const { arrow, burger, close } = icons;

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    setCurrentPath(window.location.pathname);
  }, []);

  // Escape, bloqueo del scroll de fondo y foco mientras el drawer está abierto
  useEffect(() => {
    if (!menuOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMenu();
        return;
      }

      if (event.key !== 'Tab' || !drawerRef.current) return;

      const focusables = drawerRef.current.querySelectorAll('a[href], button:not([disabled])');
      if (!focusables.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.documentElement.classList.add('--drawer-open');

    const closeButton = drawerRef.current && drawerRef.current.querySelector('.sidebar__close');
    if (closeButton) closeButton.focus();

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.documentElement.classList.remove('--drawer-open');

      if (drawerRef.current && drawerRef.current.contains(document.activeElement) && burgerRef.current) {
        burgerRef.current.focus();
      }
    };
  }, [menuOpen, closeMenu]);

  // Red de seguridad, no fuente de verdad: al cruzar a escritorio el CSS ya
  // ignora `menuOpen`, pero el focus trap de arriba no. Nunca abre, solo cierra.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;

    const query = window.matchMedia('(min-width: 992px)');
    const handleChange = (event) => {
      if (event.matches) closeMenu();
    };

    query.addEventListener('change', handleChange);

    return () => query.removeEventListener('change', handleChange);
  }, [closeMenu]);

  // Turbo vacía el nodo sin desmontar React, así que el cleanup de arriba no
  // correría al navegar. Cerramos antes de que eso pase.
  useEffect(() => {
    document.addEventListener('turbo:before-visit', closeMenu);
    document.addEventListener('turbo:before-cache', closeMenu);

    return () => {
      document.removeEventListener('turbo:before-visit', closeMenu);
      document.removeEventListener('turbo:before-cache', closeMenu);
    };
  }, [closeMenu]);

  const handleDefaultDropdown = (links) => (
    Object.values(links).some((link) => currentPath.indexOf(link) > -1)
  );

  // Cierra al tocar cualquier link del menú, incluido el de logout, que llega
  // como HTML crudo desde el helper.
  const handleDrawerClick = (event) => {
    if (event.target.closest('a')) closeMenu();
  };

  const handleTouchStart = (event) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event) => {
    if (touchStartX.current === null) return;

    const delta = event.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;

    if (delta < -60) closeMenu();
  };

  return (
    <nav className={`sidebar ${menuOpen ? '--open' : ''}`} aria-label="Navegación principal">
      <div className="sidebar__topbar">
        <button
          type="button"
          ref={burgerRef}
          className="sidebar__burger"
          aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
          aria-expanded={menuOpen}
          aria-controls="sidebar-drawer"
          onClick={() => setMenuOpen((open) => !open)}
          dangerouslySetInnerHTML={{ __html: burger }}
        />

        <a href="/" className="sidebar__logo">
          <div dangerouslySetInnerHTML={{ __html: icons.logo }} />
          <p>Sistema de Gestión</p>
        </a>
      </div>

      <div className="sidebar__overlay" onClick={closeMenu} aria-hidden="true" />

      <div
        id="sidebar-drawer"
        ref={drawerRef}
        className="sidebar__menu"
        onClick={handleDrawerClick}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className="sidebar__menu__header">
          <div className="sidebar__logo">
            <div dangerouslySetInnerHTML={{ __html: icons.logo }} />
            <p>Sistema de Gestión</p>
          </div>

          <button
            type="button"
            className="sidebar__close"
            aria-label="Cerrar menú"
            onClick={closeMenu}
            dangerouslySetInnerHTML={{ __html: close }}
          />
        </div>

        {Object.keys(groupInfo).map(key =>
          <SidebarGroup
            key={key}
            group={groupInfo[key]}
            arrow={arrow}
            currentPath={currentPath}
            defaultDropdown={handleDefaultDropdown(groupInfo[key].links)}
          />
        )}

        <div className="sidebar__user">
          <div className="sidebar__user__identity">
            <img className="sidebar__user__image" src={icons.profile} alt="" />
            <p className="sidebar__user__name">
              {[currentUser?.name, currentUser?.last_name || currentUser?.lastName].filter(Boolean).join(' ')}
            </p>
          </div>
          <div className="sidebar__user__logout" dangerouslySetInnerHTML={{ __html: logout }} />
        </div>
      </div>
    </nav>
  );
};

Sidebar.propTypes = {
  groupInfo: PropTypes.objectOf(PropTypes.object).isRequired,
  icons: PropTypes.objectOf(PropTypes.string).isRequired,
  currentUser: PropTypes.shape({
    name: PropTypes.string,
    last_name: PropTypes.string,
    lastName: PropTypes.string,
  }),
  logout: PropTypes.string,
};

export default Sidebar;
